// Shared by Cloud Functions and the frontend (imported there as @shared).
// Keep this file free of Node- or browser-only APIs.
//
// Player and team chemistry are hidden from managers (see visibility.js).
// Managers only ever see a team-level grade from chemistryGrade().

export const CHEM_MIN = 1;
export const CHEM_MAX = 10;

// Average fit between a random player and a random team (chemistry values
// uniform on 1-10). A lineup at this fit gets no chemistry effect.
export const CHEM_NEUTRAL_FIT = 0.63;

/**
 * How well one player fits the team's chemistry: 1 = perfect, 0 = worst.
 * @param {number} playerChem 1-10
 * @param {number} teamChem 1-10
 * @return {number}
 */
export function chemistryFit(playerChem, teamChem) {
  const diff = Math.abs(clampChem(playerChem) - clampChem(teamChem));
  return 1 - diff / (CHEM_MAX - CHEM_MIN);
}

/**
 * Average fit of a group of players.
 * @param {number[]} playerChems
 * @param {number} teamChem
 * @return {number} 0-1 (neutral fit when the group is empty)
 */
export function averageFit(playerChems, teamChem) {
  if (!playerChems.length) return CHEM_NEUTRAL_FIT;
  return playerChems.reduce((sum, c) => sum + chemistryFit(c, teamChem), 0) /
    playerChems.length;
}

/**
 * Chemistry edge of the five players on the floor: positive when they fit
 * better than average. The engine turns this into better shooting, better
 * defense and fewer turnovers, scaled by settings.chemistryStrength.
 * @param {number[]} playerChems
 * @param {number} teamChem
 * @return {number} about -0.6 to +0.37
 */
export function lineupEdge(playerChems, teamChem) {
  return averageFit(playerChems, teamChem) - CHEM_NEUTRAL_FIT;
}

// Grade cutoffs on whole-roster fit, set from the fit distribution of
// generated rosters: about 5% of teams get an A, 15% B-, 15% C+, 3% F.
const GRADES = [
  [0.77, "A"], [0.74, "A-"], [0.72, "B+"], [0.69, "B"], [0.65, "B-"],
  [0.61, "C+"], [0.555, "C"], [0.52, "C-"], [0.475, "D+"], [0.435, "D"],
  [0.405, "D-"], [-Infinity, "F"],
];

/**
 * The team chemistry managers see. It covers the whole roster, injured
 * players included, so swapping lineups or watching injuries can't reveal
 * which player is dragging it down.
 * @param {object[]} players roster (each with a hidden chemistry value)
 * @param {number} teamChem
 * @return {object} {grade}
 */
export function chemistryGrade(players, teamChem) {
  const fit = averageFit(players.map((p) => p.chemistry), teamChem);
  return {
    // Only the letter: an exact number would tell managers more than the
    // grade is meant to.
    grade: GRADES.find(([min]) => fit >= min)[1],
  };
}

/**
 * A letter grade as a number for ranking: F = 0 up to A = 11. Uses only
 * what managers can see, so anything built on it reveals nothing more.
 * @param {string} grade
 * @return {number} 0-11, or 5.5 (average) for an unknown grade
 */
export function gradeValue(grade) {
  const i = GRADES.findIndex(([, g]) => g === grade);
  return i === -1 ? 5.5 : GRADES.length - 1 - i;
}

/**
 * @param {number} value
 * @return {number}
 */
function clampChem(value) {
  return Math.min(CHEM_MAX, Math.max(CHEM_MIN, Number(value)));
}
